import React from 'react';
import { Box, Typography, Button, Sheet } from '@mui/joy';
import { Lock as LockIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface FeatureGateProps {
  /** Waffle flag name — must match an entry in backend core.feature_gates. */
  flag: string;
  /** Human name for the area, used in the message. */
  label: string;
  children: React.ReactNode;
}

/**
 * Render a route only when its feature flag is on.
 *
 * Flags used to drive the sidebar and nothing else, so a flagged-off feature
 * was merely hidden: typing the URL loaded a fully working page against a
 * fully working API. This is the route half of the fix; the API half is
 * FeatureFlagGateMiddleware, which reads the same flag names.
 *
 * The fallback is deliberately a plain explanation rather than a redirect —
 * someone who followed a link to a disabled area should be told why it is
 * unavailable, not silently bounced somewhere else.
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({ flag, label, children }) => {
  const enabled = useFeatureFlag(flag);
  const navigate = useNavigate();

  if (enabled) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, md: 6 } }}>
      <Sheet
        variant="soft"
        sx={{
          maxWidth: 520,
          width: '100%',
          p: 4,
          borderRadius: 'lg',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <LockIcon size={32} />
        <Typography level="h4">{label} isn’t enabled</Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          This feature is turned off for your organization. An administrator can enable it,
          or contact support if you think it should already be available.
        </Typography>
        <Button variant="solid" onClick={() => navigate('/dashboard')} sx={{ mt: 1 }}>
          Back to dashboard
        </Button>
      </Sheet>
    </Box>
  );
};

export default FeatureGate;
